import { RouteProp } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useResource from '../../../../../hooks/api/useResource';
import { getIntl } from '../../../../../utils/intl';
import ResourceDetailsHeader from './ResourceDetailsHeader';
import {
    useHasPermission,
    useIsProfileOwner,
    usePermittedDomains,
} from '../../../../v2BaseComponents/Permissions/hooks';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import { TabView, TabOption } from '../../../../v2BaseComponents/TabView';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import { isPresent } from 'ts-is-present';
import RelatedResourceList from './RelatedResourceListTab';
import useCareTeamMembers from '../../../../../hooks/api/useCareTeamMembers';
import CareTeamMemberList from './CareTeamMemberList';
import useNotes, { ResourceNoteQueryArgs } from '../../../../../hooks/api/useNotes';
import NotesList from '../../../../shared/Notes/NotesList';
import { categoryToDomain, domainToCategory } from '../../../../../utils/resourceUtils/medicalDataPermissions';
import { relatedResourcesMap } from '../../../../../utils/resourceUtils/relatedResourcesMap';
import { getCategoryLabel } from '../../../../../utils/resourceUtils/messages';
import RelatedResourceListV2 from './RelatedResourceListV2';
import { PrimaryRecordResourceCategory } from '../../../../../primary-record-types/MedicalDataTypes';
import useFeatureToggles from '../../../../../hooks/api/useFeatureToggles';
import DetailsTab from './DetailsTab';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { View } from 'react-native';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { resourceCategoryToResourceNoteEntityTypeV2 } from '../../../../shared/Notes/utils/types';
import { StandardPrompt } from '../../../../v2BaseComponents/Prompt';
import Text from '../../../../v2BaseComponents/Text';
import { useResourceDescription } from '../../../../../hooks/api/useResourceDescription';
import IconButton from '../../../../v2BaseComponents/IconButton';
import Button from '../../../../v2BaseComponents/Button';

const intl = getIntl('resource-details', {
    details: 'Details',
    header: '{category} Details',
    notes: 'Notes ({count})',
    careTeam: 'Care Team ({count})',
    condition: 'Conditions ({count})',
    allergy: 'Allergies ({count})',
    medication: 'Medications ({count})',
    visit: 'Visits ({count})',
    imaging: 'Imaging ({count})',
    procedure: 'Procedures ({count})',
    vaccination: 'Vaccinations ({count})',
    lab: 'Labs ({count})',
    description: 'Description',
    buttonTitle: 'Responses are generated by AI',
    disclaimerContent:
        'This content has been generated by an artificial intelligence language model. While we strive for accuracy and quality, please note that the information provided may not be entirely error-free or up-to-date. We recommend independently verifying the content and consulting with professionals for specific advice or information. We do not assume any responsibility or liability for the use or interpretation of this content.',
    disclaimerTitle: 'About this content',
});

export type DataDetailsScreenProps = {
    route: RouteProp<ProfilesTabParamList, 'data-details'>;
};

type ResourceTabs = 'condition' | 'allergy' | 'medication' | 'visit';
type Tabs = 'details' | 'notes' | 'care-team' | ResourceTabs;

const suggestedLinkingCategories: PrimaryRecordResourceCategory[] = ['condition', 'medication'];

const DataDetailsScreen: React.FC<DataDetailsScreenProps> = (props) => {
    const { featureToggles, isLoadingFeatureToggles } = useFeatureToggles();
    const navigator = useProfilesTabNavigation();
    const goBack = useGoBack();
    const [selectedTab, setSelectedTab] = useState<Tabs>('details');
    useEffect(() => {
        setSelectedTab('details');
    }, [props.route.params.resourceId]);
    const { profileId, category, resourceId } = props.route.params;
    useMedicalProfile({ medicalProfileId: profileId });
    const allRelatedCategories = relatedResourcesMap[category];
    const permittedViewRelatedCategories = usePermittedDomains(
        profileId,
        allRelatedCategories.map((category) => categoryToDomain(category)),
        'view',
    ).map(domainToCategory);
    const permittedEditRelatedCategories = usePermittedDomains(
        profileId,
        allRelatedCategories.map((category) => categoryToDomain(category)),
        'edit',
    ).map(domainToCategory);
    const hasCareTeamViewPermission = useHasPermission(profileId, 'careTeam', 'view');
    const hasCareTeamEditPermission = useHasPermission(profileId, 'careTeam', 'edit');
    const { resource, isLoadingResource, relatedResources, isLoadingRelatedResources, relatedResourceGroupIds } =
        useResource({
            medicalProfileId: profileId,
            resourceCategory: category,
            resourceId,
            onDeleted: () => {
                goBack();
            },
            // @ts-expect-error TS2322
            relatedResourceCategories: permittedViewRelatedCategories,
        });
    const noteQueryArgsV2: ResourceNoteQueryArgs = {
        medicalProfileId: profileId,
        entityType: resourceCategoryToResourceNoteEntityTypeV2(category),
        resourceId: resourceId,
        resourceGroupIds: [...relatedResourceGroupIds],
    };
    const v2ResourceNotes = useNotes({
        noteQueryArgs: noteQueryArgsV2,
    });

    const careTeamMembers = useCareTeamMembers({
        medicalProfileId: profileId,
        relatedResourceGroupIds,
    });

    const isOwner = useIsProfileOwner(profileId);
    const hasSourceCategoryEditPermissions = useHasPermission(profileId, categoryToDomain(category), 'edit');
    const canEditSourceResource = isOwner || hasSourceCategoryEditPermissions;
    useSetHeaderTitle(
        intl('header', {
            category: getCategoryLabel(category),
        }),
    );

    const { description, isLoadingResourceDescription, fetchResourceDescription } = useResourceDescription({
        medicalProfileId: profileId,
        resourceId,
        resourceCategory: category,
    });

    const tabOptions: TabOption[] = [
        {
            title: intl('details'),
            value: 'details',
            renderTabContent: () => (
                <DetailsTab
                    setIsOpen={(open) => {
                        fetchResourceDescription();
                        setIsOpen(open);
                    }}
                    profileId={profileId}
                    category={category}
                    resourceId={resourceId}
                />
            ),
        },
        {
            title: intl('notes', { count: v2ResourceNotes?.notes?.notes?.length || 0 }),
            value: 'notes',
            renderTabContent: () => (
                <NotesList
                    linkEmptyStateHint
                    noteQueryArgs={noteQueryArgsV2}
                    onAddNote={(args) => {
                        navigator.navigate('add-note', args);
                    }}
                    onViewDetails={(note) => {
                        navigator.navigate('note', {
                            noteId: note.noteId,
                            noteEntityType: note.entityType,
                            profileId: profileId,
                        });
                    }}
                />
            ),
        },
        hasCareTeamViewPermission
            ? {
                  title: intl('careTeam', {
                      count: careTeamMembers.isLoadingResourceGroupRelatedCareTeamMembers
                          ? '...'
                          : careTeamMembers.resourceGroupRelatedCareTeamMembers.length,
                  }),
                  value: 'care-team',
                  renderTabContent: () => (
                      <CareTeamMemberList
                          relatedCareTeamMembers={careTeamMembers.resourceGroupRelatedCareTeamMembers}
                          allCareTeamMembers={careTeamMembers.careTeamMembers}
                          profileId={profileId}
                          resourceId={resourceId}
                          isLoadingCareTeamMembers={careTeamMembers.isLoadingResourceGroupRelatedCareTeamMembers}
                          hasEditPermission={canEditSourceResource && hasCareTeamEditPermission}
                          category={category}
                          resourceGroupIds={relatedResourceGroupIds}
                      />
                  ),
              }
            : null,
    ].filter(isPresent);

    const getCategoryTabText = (category: MedicalDataTypes.PrimaryRecordResourceCategory) => {
        const count = isLoadingRelatedResources ? '...' : relatedResources[category]?.length || 0;
        return intl(category, { count });
    };

    const getRelatedResourceRenderer = (resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory) => () => {
        {
            const props = {
                profileId,
                sourceResourceCategory: category,
                resourceId,
                relatedResourceCategory: resourceCategory,
                isLoadingRelatedResources,
                hasEditPermission: canEditSourceResource && permittedEditRelatedCategories.includes(resourceCategory),
                relatedResources: relatedResources[resourceCategory],
                sourceResourceId: resourceId,
            };
            return (
                <View style={{ height: '100%', width: '100%' }}>
                    {featureToggles.smartLinking &&
                    suggestedLinkingCategories.includes(resourceCategory) &&
                    suggestedLinkingCategories.includes(category) ? (
                        <RelatedResourceListV2 key={selectedTab} {...props} />
                    ) : (
                        <RelatedResourceList {...props} />
                    )}
                </View>
            );
        }
    };

    const categorySpecificTabs: Record<MedicalDataTypes.PrimaryRecordResourceCategory, TabOption> = {
        condition: {
            title: getCategoryTabText('condition'),
            value: 'condition',
            renderTabContent: getRelatedResourceRenderer('condition'),
        },
        allergy: {
            title: getCategoryTabText('allergy'),
            value: 'allergy',
            renderTabContent: getRelatedResourceRenderer('allergy'),
        },
        medication: {
            title: getCategoryTabText('medication'),
            value: 'medication',
            renderTabContent: getRelatedResourceRenderer('medication'),
        },
        visit: {
            title: getCategoryTabText('visit'),
            value: 'visit',
            renderTabContent: getRelatedResourceRenderer('visit'),
        },
        imaging: {
            title: getCategoryTabText('imaging'),
            value: 'imaging',
            renderTabContent: getRelatedResourceRenderer('imaging'),
        },
        procedure: {
            title: getCategoryTabText('procedure'),
            value: 'procedure',
            renderTabContent: getRelatedResourceRenderer('procedure'),
        },
        vaccination: {
            title: getCategoryTabText('vaccination'),
            value: 'vaccination',
            renderTabContent: getRelatedResourceRenderer('vaccination'),
        },
        lab: {
            title: getCategoryTabText('lab'),
            value: 'lab',
            renderTabContent: getRelatedResourceRenderer('lab'),
        },
    };

    permittedViewRelatedCategories.forEach((relatedCategory) => {
        relatedCategory && tabOptions.push(categorySpecificTabs[relatedCategory]);
    });

    const [isOpen, setIsOpen] = useState(false);
    const [disclaimerOpen, setDisclaimerOpen] = useState(false);

    return (
        <AnimateLoadedContent
            key={props.route.params.resourceId}
            isLoading={isLoadingResource || !resource || isLoadingFeatureToggles}
        >
            {() => (
                <>
                    <ProfileHeaderContainer profileId={profileId}>
                        <ResourceDetailsHeader
                            resourceId={resourceId}
                            resourceCategory={category}
                            profileId={profileId}
                        />
                        <TabView
                            tabs={tabOptions}
                            selectedTab={selectedTab}
                            onChange={(tab) => setSelectedTab(tab as Tabs)}
                        />
                        <StandardPrompt
                            isOpen={isOpen}
                            setIsOpen={(isOpen) => !isOpen && setIsOpen(false)}
                            title={intl('description')}
                            testID={'resource-description-prompt'}
                            headerAction={<IconButton icon="X" onPress={() => setIsOpen(false)} />}
                        >
                            <AnimateLoadedContent isLoading={isLoadingResourceDescription}>
                                {() => (
                                    <>
                                        <Text variant="h5">{description}</Text>
                                        <Button
                                            variant="list-button"
                                            icon="Info"
                                            title={intl('buttonTitle')}
                                            onPress={() => {
                                                setDisclaimerOpen(!disclaimerOpen);
                                            }}
                                            testID={'disclaimer-button'}
                                            style={{ marginTop: 10, marginBottom: 10 }}
                                        />
                                        {disclaimerOpen && (
                                            <Text variant="body-1" testID={'disclaimer-content'}>
                                                {intl('disclaimerContent')}
                                            </Text>
                                        )}
                                    </>
                                )}
                            </AnimateLoadedContent>
                        </StandardPrompt>
                    </ProfileHeaderContainer>
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(DataDetailsScreen);
